import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  ScrollView,
  Platform,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { DLONotification } from '../types/notification.types';

type TabType = 'all' | 'unread';

const MESSAGE_PREVIEW_LENGTH = 200;

const getTruncatedMessage = (message: string): string =>
  message && message.length > MESSAGE_PREVIEW_LENGTH
    ? `${message.slice(0, MESSAGE_PREVIEW_LENGTH)}...`
    : message;

interface NotificationOverlayProps {
  visible: boolean;
  onClose: () => void;
  notifications: DLONotification[];
  onMarkAsRead: (notificationId: number, userGpid: string) => void;
  onMarkAllAsRead: (userGpid: string) => void;
  onDismissAll: () => void;
  userGpid: string;
  loading?: boolean;
  onNavigate?: (screen: string, params?: { [key: string]: any }) => void;
  onDispatchAction?: (action: { type: string; payload: any }) => void;
}

const NotificationOverlay: React.FC<NotificationOverlayProps> = ({
  visible,
  onClose,
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDismissAll,
  userGpid,
  loading = false,
  onNavigate,
  onDispatchAction,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const isDeletedActionPlan = (notification: DLONotification) =>
    notification.ACTION_PLAN_STATUS?.toLowerCase() === 'deleted';

  const getNotificationTitle = (notification: DLONotification) => {
    if (isDeletedActionPlan(notification)) {
      return /deleted/i.test(notification.TITLE)
        ? notification.TITLE
        : `${notification.TITLE} (deleted)`;
    }

    return notification.TITLE;
  };

  const normalizeActionPlanId = (value: DLONotification['ACTION_PLAN_ID']): string | null => {
    if (Array.isArray(value)) {
      const first = value.find(v => v !== null && v !== undefined && String(v).trim() !== '');
      return first !== undefined ? String(first) : null;
    }
    if (value === null || value === undefined) return null;
    const s = String(value).trim();
    return s ? s : null;
  };

  const handleNotificationPress = (notification: DLONotification) => {
    if (!notification.IS_READ) {
      onMarkAsRead(notification.ID, userGpid);
    }

    if (isDeletedActionPlan(notification)) {
      return;
    }

    // Handle ticket notifications - when ACTION_PLAN_ID is null but TICKET_ID exists
    if (notification.TICKET_ID && !notification.ACTION_PLAN_ID) {
      onClose();
      // Dispatch Redux action to set selected ticket
      if (onDispatchAction) {
        onDispatchAction({
          type: 'ticketing/setSelectedTicketId',
          payload: notification.TICKET_ID,
        });
        onDispatchAction({
          type: 'ticketing/setTicketDetailSource',
          payload: 'My Tickets',
        });
      }
      // Navigate to ticket detail screen
      if (onNavigate) {
        onNavigate('Ticket Detail');
      }
      return;
    }

    const actionPlanId = normalizeActionPlanId(notification.ACTION_PLAN_ID);
    const isStandUp = Boolean(notification.IS_STANDUP);
    onClose();

    if (onNavigate && actionPlanId) {
      if (isStandUp) {
        onNavigate('Stand Up', {
          initialRoute: 'TopOpportunityDetails',
          actionPlanId,
        });
        return;
      }

      onNavigate('Action Plan', {
        initialRoute: 'ActionPlanDetails',
        actionPlanId,
      });
    }
  };

  // Filter notifications based on active tab
  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter(n => !n.IS_READ);
    }
    return notifications;
  }, [notifications, activeTab]);

  // Count unread notifications
  const unreadCount = useMemo(() => {
    return notifications.filter(n => !n.IS_READ).length;
  }, [notifications]);

  // Format date to be more readable
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  // Get notification type icon color
  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'NEW_ACTION_PLAN_ASSIGNED':
        return '#0065C3'; // Blue
      case 'ACTION_PLAN_UPDATED':
        return '#F59E0B'; // Amber
      case 'DEADLINE_EXTENDED':
        return '#10B981'; // Green
      case 'ACTION_PLAN_DELETED':
        return '#D40020'; // Red
      default:
        return '#818181'; // Gray
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        {/* Backdrop tap to close */}
        <TouchableOpacity
          style={StyleSheet.absoluteFillObject}
          activeOpacity={1}
          onPress={onClose}
        />
        <View style={styles.container}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Notifications</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Svg width={24} height={24} viewBox="0 0 24 24" fill="none">
                <Path
                  d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                  fill="#FFFFFF"
                />
              </Svg>
            </TouchableOpacity>
          </View>

          {/* Tabs */}
          <View style={styles.tabsContainer}>
            <TouchableOpacity
              style={[
                styles.tab,
                activeTab === 'all' && styles.activeTab,
              ]}
              onPress={() => setActiveTab('all')}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === 'all' && styles.activeTabText,
                ]}
              >
                All
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.tab,
                activeTab === 'unread' && styles.activeTab,
              ]}
              onPress={() => setActiveTab('unread')}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === 'unread' && styles.activeTabText,
                ]}
              >
                Unread
              </Text>
              {unreadCount > 0 && (
                <View style={styles.badge}>
                  <Text style={styles.badgeText}>{unreadCount}</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView 
            style={styles.scrollView}
            contentContainerStyle={styles.scrollContent}
            showsVerticalScrollIndicator={true}
          >
            {loading ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#0065C3" />
                <Text style={styles.loadingText}>Loading notifications...</Text>
              </View>
            ) : filteredNotifications.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Svg width={48} height={48} viewBox="0 0 24 24" fill="none">
                  <Path 
                    d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" 
                    fill="#818181"
                  />
                </Svg>
                <Text style={styles.emptyText}>No notifications</Text>
                <Text style={styles.emptySubtext}>You're all caught up!</Text>
              </View>
            ) : (
              filteredNotifications.map((notification) => (
                <TouchableOpacity
                  key={notification.ID}
                  style={[
                    styles.notificationRow,
                    isDeletedActionPlan(notification) && styles.disabledNotificationRow,
                  ]}
                  onPress={() => handleNotificationPress(notification)}
                  activeOpacity={isDeletedActionPlan(notification) ? 1 : 0.7}
                >
                  <View style={styles.rowContent}>
                    {/* Unread indicator dot */}
                    {!notification.IS_READ && (
                      <View
                        style={[
                          styles.unreadIndicator,
                          isDeletedActionPlan(notification) && styles.disabledUnreadIndicator,
                        ]}
                      />
                    )}
                    
                    <View style={styles.notificationContent}>
                      {/* Title */}
                      <Text
                        style={[
                          styles.notificationTitle,
                          !notification.IS_READ && styles.unreadTitle,
                          isDeletedActionPlan(notification) && styles.disabledNotificationTitle,
                        ]}
                      >
                        {getNotificationTitle(notification)}
                      </Text>
                      
                      {/* Message */}
                      <Text
                        style={[
                          styles.notificationMessage,
                          isDeletedActionPlan(notification) && styles.disabledNotificationMessage,
                        ]}
                      >
                        {getTruncatedMessage(notification.MESSAGE)}
                      </Text>
                    </View>
                  </View>
                  
                  {/* Divider */}
                  <View style={styles.divider} />
                </TouchableOpacity>
              ))
            )}
          </ScrollView>

          {/* Footer Actions */}
          {!loading && notifications.length > 0 && (
            <View style={styles.footer}>
              <TouchableOpacity
                style={styles.dismissButton}
                onPress={onDismissAll}
              >
                <Svg width={20} height={20} viewBox="0 0 24 24" fill="none">
                  <Path
                    d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"
                    fill="#005CBC"
                  />
                </Svg>
                <Text style={styles.dismissButtonText}>Dismiss all</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={styles.markAllButton}
                onPress={() => onMarkAllAsRead(userGpid)}
              >
                <Text style={styles.markAllButtonText}>Mark all as read</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
};

// iPad: iOS device with width >= 768. Gets the same compact popup as web
// (fixed 320×488) rather than the near-full-screen mobile treatment.
const { width: _screenWidth, height: _screenHeight } = Dimensions.get('window');
const isIPad = Platform.OS === 'ios' && _screenWidth >= 768;

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    alignItems: 'flex-end',
    paddingTop: (Platform.OS === 'web' || isIPad) ? 64 : 118,
    paddingRight: (Platform.OS === 'web' || isIPad) ? 40 : 16,
  },
  container: {
    width: (Platform.OS === 'web' || isIPad) ? 320 : _screenWidth * 0.9,
    height: (Platform.OS === 'web' || isIPad) ? 488 : _screenHeight * 0.65,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.08,
    shadowRadius: 24,
    elevation: 8,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
  },
  headerTitle: {
    color: '#2D2D2D',
    fontSize: 18,
    fontWeight: '700',
    lineHeight: 28,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  closeButton: {
    padding: 4,
  },
  tabsContainer: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#D8E7FB',
    paddingHorizontal: 16,
  },
  tab: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    gap: 8,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    marginRight: 16,
  },
  activeTab: {
    borderBottomColor: '#034895',
  },
  tabText: {
    color: '#4B4B4B',
    fontSize: 14,
    lineHeight: 20,
    fontWeight: '400',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  activeTabText: {
    color: '#4B4B4B',
    fontWeight: '700',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  badge: {
    backgroundColor: '#B52C07',
    paddingHorizontal: 4,
    minHeight: 16,
    borderRadius: 99,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    lineHeight: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 16,
  },
  loadingContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: 16,
    color: '#4B4B4B',
    fontSize: 14,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  emptyContainer: {
    padding: 40,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    marginTop: 16,
    color: '#2D2D2D',
    fontSize: 16,
    fontWeight: '600',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-SemiBold',
    }),
  },
  emptySubtext: {
    marginTop: 8,
    color: '#4B4B4B',
    fontSize: 14,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  notificationRow: {
    paddingVertical: 12,
    paddingHorizontal: 0,
  },
  disabledNotificationRow: {
    backgroundColor: '#F7F7F7',
  },
  rowContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 16,
    gap: 4,
  },
  unreadIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#034895',
    marginTop: 6,
  },
  disabledUnreadIndicator: {
    backgroundColor: '#A0A0A0',
  },
  notificationContent: {
    flex: 1,
    gap: 2,
    paddingRight: 8,
  },
  notificationTitle: {
    color: '#2D2D2D',
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Medium',
    }),
  },
  disabledNotificationTitle: {
    color: '#6E6E6E',
  },
  unreadTitle: {
    fontWeight: '700',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  notificationMessage: {
    color: '#4B4B4B',
    fontSize: 12,
    lineHeight: 18,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
    }),
  },
  disabledNotificationMessage: {
    color: '#8A8A8A',
  },
  divider: {
    height: 1,
    backgroundColor: '#D8E7FB',
    marginLeft: 16,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#D8E7FB',
    backgroundColor: '#FFFFFF',
    shadowColor: '#005EC0',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 24,
    elevation: 3,
  },
  dismissButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
    paddingHorizontal: 0,
  },
  dismissButtonText: {
    color: '#034895',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
  markAllButton: {
    paddingVertical: 4,
    paddingHorizontal: 16,
    borderWidth: 1,
    borderColor: '#034895',
    borderRadius: 24,
    backgroundColor: '#FFFFFF',
  },
  markAllButtonText: {
    color: '#034895',
    fontSize: 14,
    fontWeight: '700',
    lineHeight: 20,
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
    }),
  },
});

export default NotificationOverlay;
